import { InspectionStatusInfoButton } from "@/components/inspection-status-info-panel";
import { DetailsTileTitle } from "@/components/license-details/tile-info";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Select, type SelectOption } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAppForm } from "@/components/ui/tanstack-form";
import { UserType } from "@/constants";
import { getAdminUsersOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { updateInspection } from "@/hera-api/client/sdk.gen";
import type {
  InspectionResponseSchema,
  InspectionStatus,
  InspectionWithLicenseResponseSchema,
  LicenseResponseSchema,
  QuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useGovernmentPortalJurisdictionId } from "@/hooks/use-government-portal-jurisdiction-id";
import { isGovernmentPortalViewer } from "@/lib/government-impersonate";
import {
  ALL_INSPECTION_STATUSES,
  formatDateNoTimezone,
  formatDateTime,
  getDashboardLink,
  getInspectionStatusDisplay,
  getInspectionStatusLabel,
  getQueryErrorMessage,
  getUserType,
  inspectionPaymentsHasAwaitingCheckPayment,
  inspectionPaymentsHasAwaitingInvoicePayment,
  isInspectionStatusEditable,
} from "@/lib/utils";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ChevronDown,
  Edit,
  RotateCcw,
  Save,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { QuestionField } from "../../registrant/form-utils/question-field-utils";
import { InspectionAnswersPanel } from "./inspection-answers-panel";
import type { PhotoAnswerReviewProperty } from "./photo-answer-review";

// Type that works with both inspection schemas
type InspectionDisplayData = Pick<
  InspectionResponseSchema | InspectionWithLicenseResponseSchema,
  | "id"
  | "status"
  | "effective_date"
  | "effective_end_date"
  | "cancellation_date"
  | "cancellation_reason"
  | "created_at"
  | "assignee_id"
> & {
  answers_to_additional_questions?: Record<string, unknown> | null;
};

// Schema for inspection form validation
const InspectionInfoSchema = z.object({
  status: z.enum(
    ALL_INSPECTION_STATUSES as [InspectionStatus, ...InspectionStatus[]],
  ),
  effective_date: z.string().nullable(),
  effective_end_date: z.string().nullable(),
});

interface EditableInspectionInfoProps {
  inspection: InspectionDisplayData;
  onSave: () => void;
  onReset: () => void;
}

function EditableInspectionInfo({
  inspection,
  onSave,
  onReset,
}: EditableInspectionInfoProps) {
  const queryClient = useQueryClient();
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus on the first input when the component mounts
    const timer = setTimeout(() => {
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const form = useAppForm({
    defaultValues: {
      status: inspection.status,
      effective_date: inspection.effective_date || null,
      effective_end_date: inspection.effective_end_date || null,
    },
    validators: {
      onSubmit: InspectionInfoSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateInspection({
          path: {
            inspection_id: inspection.id,
          },
          body: {
            status: value.status as InspectionStatus | undefined,
            effective_date: value.effective_date || null,
            effective_end_date: value.effective_end_date || null,
          },
        });
        queryClient.invalidateQueries({
          queryKey: ["inspections"],
        });
        queryClient.invalidateQueries({
          queryKey: ["licenseDetails"],
        });
        toast({
          title: "Success",
          description: "Inspection information updated successfully",
          variant: "secondary",
        });
        onSave();
      } catch (error) {
        toast({
          title: "Error",
          description: `${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
      }
    },
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  const handleReset = () => {
    form.reset();
    onReset();
  };

  return (
    <div className="space-y-4">
      <form.AppForm>
        <form className="space-y-4" onSubmit={handleSave}>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-bold">Created</p>
              <p className="text-sm">{formatDateTime(inspection.created_at)}</p>
            </div>

            <form.AppField name="status">
              {(field) => {
                // Get all inspection statuses
                const allStatuses = Array.from(ALL_INSPECTION_STATUSES);

                // Ensure current value is included
                const currentValue = field.state.value as string;
                const allStatusesSet = new Set(allStatuses);
                if (currentValue && !allStatusesSet.has(currentValue as any)) {
                  allStatusesSet.add(currentValue as any);
                }

                // Map to options with user-friendly labels
                const answerOptions = Array.from(allStatusesSet).map(
                  (status) => ({
                    value: status,
                    label: getInspectionStatusLabel(status),
                  }),
                );

                return (
                  <FormFieldWrapper field={field} label="Status">
                    <QuestionField
                      answerType="single_select"
                      field={field}
                      answerOptions={answerOptions}
                    />
                  </FormFieldWrapper>
                );
              }}
            </form.AppField>

            <form.AppField name="effective_date">
              {(field) => (
                <FormFieldWrapper field={field} label="Inspection Date">
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField name="effective_end_date">
              {(field) => (
                <FormFieldWrapper field={field} label="Valid Until">
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              className="min-w-[80px]"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" className="min-w-[80px]">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
}

interface InspectionDisplayProps {
  inspection: InspectionDisplayData;
  index?: number;
  showBorder?: boolean;
  userType?: UserType;
  onInspectorUpdated?: () => void;
  inspectionJurisdictionId?: string | null;
  onClick?: () => void;
  forceEdit?: boolean;
  onEditModeChange?: (isEditing: boolean) => void;
  /** When provided, EXEMPT inspections show "Awaiting Registration Approval" until license is not PAID_PENDING_REVIEW. */
  licenseStatus?: string;
  /** Show (i) button next to inspection status (e.g. in inspection details pane; not in lists). */
  showStatusInfoButton?: boolean;
  /** When the parent row has list-level flags (inspection dashboard) or a trimmed inspection object. */
  hasAwaitingCheckPaymentOverride?: boolean;
  hasAwaitingInvoicePaymentOverride?: boolean;
  /** Inspection-type questions used to render submitted answers. */
  inspectionQuestions?: QuestionResponseSchema[];
  /** When true, answers start collapsed (older history rows). */
  answersCollapsedByDefault?: boolean;
  /** Prefer elevated lightbox when nested in a Sheet. */
  elevatedAnswerDialog?: boolean;
  /** Property for photo answer maps/captions. */
  property?: PhotoAnswerReviewProperty | null;
}

export function InspectionDisplay({
  inspection,
  index,
  showBorder = false,
  userType,
  onInspectorUpdated,
  inspectionJurisdictionId,
  onClick,
  forceEdit = false,
  onEditModeChange,
  licenseStatus,
  showStatusInfoButton = false,
  hasAwaitingCheckPaymentOverride,
  hasAwaitingInvoicePaymentOverride,
  inspectionQuestions,
  answersCollapsedByDefault = false,
  elevatedAnswerDialog = false,
  property = null,
}: InspectionDisplayProps) {
  const isRegistrationApproved =
    licenseStatus !== undefined && licenseStatus !== "PAID_PENDING_REVIEW";
  const row = inspection as Partial<InspectionWithLicenseResponseSchema>;
  const hasListPaymentFlags =
    "has_awaiting_check_payment" in inspection ||
    "has_awaiting_invoice_payment" in inspection;
  const hasAwaitingCheckPayment =
    hasAwaitingCheckPaymentOverride !== undefined
      ? hasAwaitingCheckPaymentOverride
      : hasListPaymentFlags
        ? Boolean(row.has_awaiting_check_payment)
        : inspectionPaymentsHasAwaitingCheckPayment(
            (inspection as InspectionResponseSchema).payments,
          );
  const hasAwaitingInvoicePayment =
    hasAwaitingInvoicePaymentOverride !== undefined
      ? hasAwaitingInvoicePaymentOverride
      : hasListPaymentFlags
        ? Boolean(row.has_awaiting_invoice_payment)
        : inspectionPaymentsHasAwaitingInvoicePayment(
            (inspection as InspectionResponseSchema).payments,
          );
  const inspectionDisplay = getInspectionStatusDisplay(
    inspection.status,
    hasAwaitingCheckPayment,
    licenseStatus !== undefined ? isRegistrationApproved : undefined,
    hasAwaitingInvoicePayment,
  );
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { user: auth0User } = useAuth0();
  const queryClient = useQueryClient();
  const [isInspectorDialogOpen, setIsInspectorDialogOpen] = useState(false);
  const [selectedInspectorId, setSelectedInspectorId] = useState<string[]>(
    inspection.assignee_id ? [inspection.assignee_id] : [],
  );
  const [answersExpanded, setAnswersExpanded] = useState(
    !answersCollapsedByDefault,
  );

  // Get current user type
  const currentUserType = userType ?? getUserType(auth0User);
  const { jurisdictionId: governmentJurisdictionId } =
    useGovernmentPortalJurisdictionId(
      isGovernmentPortalViewer(currentUserType),
    );

  // Fetch all users
  const { data: usersData } = useQuery({
    ...getAdminUsersOptions({
      query: {
        include_deleted: false,
      },
    }),
    enabled:
      isAuthSetupComplete &&
      (currentUserType === UserType.OPERATOR ||
        currentUserType === UserType.GOVERNMENT),
  });

  // Filter users to only show government users for the inspection's jurisdiction
  const availableUsers = useMemo(() => {
    if (!usersData?.users) return [];

    // Determine the jurisdiction ID to filter by
    const jurisdictionIdToFilter =
      inspectionJurisdictionId || governmentJurisdictionId;

    if (!jurisdictionIdToFilter) return [];

    // Filter to only government users for the inspection's jurisdiction
    return usersData.users.filter(
      (u) =>
        u.user_type === "government" &&
        u.jurisdiction?.id === jurisdictionIdToFilter,
    );
  }, [usersData, inspectionJurisdictionId, governmentJurisdictionId]);

  // Create select options
  const inspectorOptions: SelectOption[] = useMemo(() => {
    return availableUsers.map((user) => ({
      value: user.id,
      label: user.name || user.email || "Unknown",
    }));
  }, [availableUsers]);

  // Get current inspector name
  const currentInspector = useMemo(() => {
    if (!inspection.assignee_id || !usersData?.users) return null;
    return usersData.users.find((u) => u.id === inspection.assignee_id);
  }, [inspection.assignee_id, usersData]);

  // Mutation to update inspector
  const updateInspectorMutation = useMutation({
    mutationFn: async (assigneeId: string | null) => {
      return await updateInspection({
        path: {
          inspection_id: inspection.id,
        },
        body: {
          assignee_id: assigneeId,
        },
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Inspector updated successfully",
        variant: "secondary",
      });
      queryClient.invalidateQueries({
        queryKey: ["inspections"],
      });
      queryClient.invalidateQueries({
        queryKey: ["licenseDetails"],
      });
      setIsInspectorDialogOpen(false);
      onInspectorUpdated?.();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update inspector: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    },
  });

  const reviewInspectionMutation = useMutation({
    mutationFn: async (nextStatus: "COMPLIANT" | "INSPECTION_PENDING") => {
      return await updateInspection({
        path: { inspection_id: inspection.id },
        body: { status: nextStatus },
      });
    },
    onSuccess: (_data, nextStatus) => {
      toast({
        title: "Success",
        description:
          nextStatus === "COMPLIANT"
            ? "Inspection approved"
            : "Inspection rejected — returned to pending",
        variant: "secondary",
      });
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
      queryClient.invalidateQueries({ queryKey: ["licenseDetails"] });
      onInspectorUpdated?.();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handleSaveInspector = () => {
    const assigneeId = selectedInspectorId[0] || null;
    updateInspectorMutation.mutate(assigneeId);
  };

  const canReviewCommitted =
    (currentUserType === UserType.OPERATOR ||
      currentUserType === UserType.GOVERNMENT) &&
    inspection.status === "COMMITTED";

  const canEditInspection =
    (currentUserType === UserType.OPERATOR ||
      currentUserType === UserType.GOVERNMENT) &&
    isInspectionStatusEditable(inspection.status) &&
    inspectionDisplay.label !== "Awaiting Registration Approval";

  const canEditInspector = canEditInspection;

  const [isEditing, setIsEditing] = useState(forceEdit);

  useEffect(() => {
    if (forceEdit && !isEditing) {
      setIsEditing(true);
    }
  }, [forceEdit, isEditing]);

  if (isEditing && canEditInspection) {
    return (
      <EditableInspectionInfo
        inspection={inspection}
        onSave={() => {
          setIsEditing(false);
          onEditModeChange?.(false);
          onInspectorUpdated?.();
        }}
        onReset={() => {
          setIsEditing(false);
          onEditModeChange?.(false);
        }}
      />
    );
  }

  return (
    <>
      <div
        className={`${showBorder ? "border-t pt-6" : ""} ${onClick ? "cursor-pointer hover:bg-muted/30 rounded-md p-2 -m-2 transition-colors" : ""}`}
        onClick={onClick}
        onKeyDown={(e) => {
          if (onClick && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onClick();
          }
        }}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <div className="grid gap-4 grid-cols-1 @min-[450px]:grid-cols-2 items-start">
          <div>
            {index !== undefined ? (
              <>
                <p className="text-sm font-bold">Inspection #{index + 1}</p>
                <p className="text-sm text-muted-foreground">
                  Created: {formatDateTime(inspection.created_at)}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold">Inspection</p>
                <p className="text-sm text-muted-foreground">
                  Created: {formatDateTime(inspection.created_at)}
                </p>
              </>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-bold">Status</p>
              {showStatusInfoButton && <InspectionStatusInfoButton />}
            </div>
            <Badge variant={inspectionDisplay.variant}>
              {inspectionDisplay.label}
            </Badge>
          </div>

          <div>
            <p className="text-sm font-bold">Inspection Date</p>
            <p className="text-sm">
              {inspection.effective_date
                ? formatDateNoTimezone(inspection.effective_date)
                : "N/A"}
            </p>
          </div>

          <div>
            <p className="text-sm font-bold">Valid Until</p>
            <p className="text-sm">
              {inspection.effective_end_date
                ? formatDateNoTimezone(inspection.effective_end_date)
                : "N/A"}
            </p>
          </div>

          <div>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <p className="text-sm font-bold">Inspector</p>
                <p className="text-sm">
                  {currentInspector
                    ? currentInspector.name ||
                      currentInspector.email ||
                      "Unknown"
                    : "Not assigned"}
                </p>
              </div>
              {canEditInspector && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  aria-label="Edit Inspector..."
                  onClick={() => {
                    setSelectedInspectorId(
                      inspection.assignee_id ? [inspection.assignee_id] : [],
                    );
                    setIsInspectorDialogOpen(true);
                  }}
                >
                  <Edit className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          {inspection.cancellation_date && (
            <div>
              <p className="text-sm font-bold">Cancellation Date</p>
              <p className="text-sm">
                {formatDateNoTimezone(inspection.cancellation_date)}
              </p>
            </div>
          )}

          {inspection.cancellation_reason && (
            <div className="col-span-full">
              <p className="text-sm font-bold">Cancellation Reason</p>
              <p className="text-sm">{inspection.cancellation_reason}</p>
            </div>
          )}
        </div>

        {inspectionQuestions &&
        inspectionQuestions.length > 0 &&
        (currentUserType === UserType.OPERATOR ||
          currentUserType === UserType.GOVERNMENT) ? (
          <div
            className="mt-4 space-y-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {answersCollapsedByDefault ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-0 text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setAnswersExpanded((open) => !open)}
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${answersExpanded ? "rotate-180" : ""}`}
                />
                {answersExpanded ? "Hide Answers" : "Show Answers"}
              </Button>
            ) : (
              <p className="text-sm font-bold">Answers</p>
            )}
            {answersExpanded ? (
              <InspectionAnswersPanel
                questions={inspectionQuestions}
                answers={inspection.answers_to_additional_questions}
                elevatedDialog={elevatedAnswerDialog}
                property={property}
              />
            ) : null}
          </div>
        ) : null}

        {canReviewCommitted ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="default"
              size="sm"
              disabled={reviewInspectionMutation.isPending}
              onClick={(e) => {
                e.stopPropagation();
                reviewInspectionMutation.mutate("COMPLIANT");
              }}
            >
              <ThumbsUp className="h-4 w-4" />
              Approve Inspection
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={reviewInspectionMutation.isPending}
              onClick={(e) => {
                e.stopPropagation();
                if (
                  confirm(
                    "Reject this inspection and return it to Inspection Pending?",
                  )
                ) {
                  reviewInspectionMutation.mutate("INSPECTION_PENDING");
                }
              }}
            >
              <ThumbsDown className="h-4 w-4" />
              Reject Inspection
            </Button>
          </div>
        ) : null}
      </div>

      {/* Inspector Selection Dialog */}
      {canEditInspector && (
        <Dialog
          open={isInspectorDialogOpen}
          onOpenChange={setIsInspectorDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select Inspector</DialogTitle>
              <DialogDescription>
                Choose an inspector to assign to this inspection.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select
                options={inspectorOptions}
                value={selectedInspectorId}
                onValueChange={setSelectedInspectorId}
                placeholder="Select an inspector..."
                searchPlaceholder="Search inspectors..."
                emptyMessage="No inspectors found."
                multiple={false}
              />
            </div>
            <div className="py-2">
              <Link
                to={getDashboardLink(currentUserType)}
                onClick={() => {
                  // Set the selected tab to "users" in session storage
                  if (currentUserType === UserType.OPERATOR) {
                    sessionStorage.set(
                      SESSION_KEYS.OPERATOR_DASHBOARD_SELECTED_TAB,
                      "users",
                    );
                  } else if (currentUserType === UserType.GOVERNMENT) {
                    sessionStorage.set(
                      SESSION_KEYS.GOVERNMENT_DASHBOARD_SELECTED_TAB,
                      "users",
                    );
                  }
                  // Close the dialog
                  setIsInspectorDialogOpen(false);
                }}
                className="text-xs text-muted-foreground hover:text-primary underline cursor-pointer"
              >
                New inspectors can be added in the Users list.
              </Link>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsInspectorDialogOpen(false)}
                disabled={updateInspectorMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveInspector}
                disabled={updateInspectorMutation.isPending}
              >
                {updateInspectorMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

interface InspectionsCardProps {
  data: LicenseResponseSchema;
  userType?: UserType;
  onInspectorUpdated?: () => void;
  onInspectionClick?: (inspectionId: string) => void;
  /** All license-type questions; inspection-type ones are used for answer review. */
  questions?: QuestionResponseSchema[];
}

export function InspectionsCard({
  data,
  userType,
  onInspectorUpdated,
  onInspectionClick,
  questions,
}: InspectionsCardProps) {
  const inspections = data.inspections || [];

  const inspectionQuestions = useMemo(
    () => (questions ?? []).filter((q) => q.question_type === "inspection"),
    [questions],
  );

  if (inspections.length === 0) {
    return null;
  }

  // Prefer most recent by inspection date, then created_at
  const sortedInspections = [...inspections].sort((a, b) => {
    const aDate = a.effective_date || a.created_at;
    const bDate = b.effective_date || b.created_at;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  const mostRecent = sortedInspections[0];
  const mostRecentDateLabel = mostRecent?.effective_date
    ? formatDateNoTimezone(mostRecent.effective_date)
    : mostRecent
      ? formatDateTime(mostRecent.created_at)
      : null;

  return (
    <Card className="gap-4">
      <CardHeader>
        <DetailsTileTitle name="Inspections">
          Inspections ({inspections.length})
        </DetailsTileTitle>
        {mostRecentDateLabel ? (
          <p className="text-sm text-muted-foreground">
            Most recent inspection: {mostRecentDateLabel}
          </p>
        ) : null}
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {sortedInspections.map((inspection, index) => (
            <InspectionDisplay
              key={inspection.id}
              inspection={inspection}
              index={index}
              showBorder={index !== 0}
              userType={userType}
              onInspectorUpdated={onInspectorUpdated}
              inspectionJurisdictionId={data.jurisdiction?.id}
              licenseStatus={data.status}
              onClick={
                onInspectionClick
                  ? () => onInspectionClick(inspection.id)
                  : undefined
              }
              showStatusInfoButton={
                userType === UserType.OPERATOR ||
                userType === UserType.GOVERNMENT
              }
              inspectionQuestions={inspectionQuestions}
              answersCollapsedByDefault={index > 0}
              property={data.property}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
